import { mskDayMonth } from '../ui/moscow';
/**
 * Полоса о сроке подписки (FR-5.3).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОДПИСЬ В ЯЩИКЕ — НЕ ПРЕДУПРЕЖДЕНИЕ. ЯЩИК ЗАКРЫТ.
 *
 * Строка «осталось 4 суток» под пунктом «Тариф и оплата» появляется,
 * когда человек ОТКРЫЛ меню, — а он открывает его раз в месяц. Про
 * заморозку он узнаёт от того, что кампании перестали слушаться,
 * и приходит в поддержку с «у вас сломалось».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЛОСА ГОРАЗДО ГРОМЧЕ ПОДПИСИ, ПОЭТОМУ ЗАЖИГАЕТСЯ ПОЗЖЕ.
 *
 * Подпись говорит с десяти суток (`hint.ts`), полоса — с трёх (ТЗ,
 * FR-5.3). Полоса, висящая над каждым экраном десять дней подряд,
 * перестаёт читаться к четвёртому — и не сработает в тот единственный
 * день, ради которого заведена.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * В ТЕКСТЕ — ДАТА, А НЕ «ЧЕРЕЗ N СУТОК».
 *
 * Суток остаётся `floor((конец − сейчас) / 24ч)`. Подписка кончается
 * завтра в девять утра, сейчас три дня — остаток 18 часов, ноль суток,
 * и «кончается сегодня» будет неправдой. Дата не округляется никогда
 * и вопросов не вызывает.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** С какого дня зажигается полоса (ТЗ, FR-5.3). */
export const BAND_FROM_DAYS = 3;

export interface BandLine {
  level: 'warn' | 'bad';
  text: string;
}

export interface PlanState {
  health: 'active' | 'ending' | 'expired';
  daysLeft: number | null;
  frozen: boolean;
  reason: string | null;
  expiresAt: string | null;
}

export function bandFor(current: PlanState | null): BandLine | null {
  /**
   * ПОДПИСКИ НЕТ — ПОЛОСЫ НЕТ.
   *
   * Так выглядит пространство до подключения кабинета: пробный ещё
   * не выдан, и встречать новичка красной полосой значит сообщать
   * о поломке там, где всё идёт по порядку.
   */
  if (current === null) return null;

  // ЗАМОРОЗКА ВАЖНЕЕ СРОКА: она уже случилась, а срок только идёт.
  // Причину считает сервер — там же, где принимает решение
  if (current.frozen) {
    return {
      level: 'bad',
      text: head(current.reason) ?? 'Управление кампаниями остановлено.',
    };
  }

  if (current.health === 'expired') {
    return { level: 'bad', text: `Подписка кончилась${on(current.expiresAt)}.` };
  }

  // Бессрочная подписка молчит: сказать о ней нечего
  if (current.daysLeft === null) return null;

  if (current.daysLeft <= BAND_FROM_DAYS) {
    return { level: 'warn', text: `Подписка кончается${on(current.expiresAt)}.` };
  }

  return null;
}

/**
 * Первая фраза длинной причины.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПРИЧИНА ЗАМОРОЗКИ — ЭТО АБЗАЦ, А ПОЛОСА — ЭТО СТРОКА.
 *
 * Сервер собирает причину полностью: что случилось, что замерло, что
 * цело и что делать. Это правильный текст для экрана тарифа, где его
 * читают сидя. В полосе над каждым экраном он занимает три строки,
 * и первое, что человек делает, — перестаёт её видеть.
 *
 * Целиком причина никуда не девается: полоса ведёт на экран тарифа,
 * где она и написана.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function head(reason: string | null): string | null {
  if (reason === null) return null;

  const text = reason.trim();
  if (text === '') return null;
  if (text.length <= LONG) return text;

  const first = text.split(/(?<=[.!?])\s+/)[0] ?? text;

  // ОБРЫВОК КОРОЧЕ ЭТОГО — НЕ ФРАЗА, А СОКРАЩЕНИЕ («т. е.», «ул.»).
  // Лучше показать длинно, чем показать бессмыслицу
  return first.length >= SHORTEST ? first : text;
}

/** Длиннее этого — режем. Примерно строка полосы на обычном экране. */
const LONG = 120;
/** Короче этого первая «фраза» — это сокращение, а не фраза. */
const SHORTEST = 25;

/** « 23 августа» или пусто, если даты нет. Пробел спереди — намеренно. */
function on(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return ' ' + mskDayMonth(d);
}
